import { useState, useRef, useEffect, useCallback } from 'react';

// Session flag: documentIds for which the user dismissed email-forwarding guidance (one sessionStorage key)
const EMAIL_FORWARD_WARNING_SHOWN_KEY = 'medprompt_emailForwardWarningShown';

function getEmailForwardWarningShownDocIds() {
  try {
    const raw = sessionStorage.getItem(EMAIL_FORWARD_WARNING_SHOWN_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id) => typeof id === 'string' && id.length > 0)
      : [];
  } catch {
    return [];
  }
}

function isEmailForwardWarningMarkedShown(documentId) {
  if (!documentId) return false;
  return getEmailForwardWarningShownDocIds().includes(documentId);
}

function markEmailForwardWarningShown(documentId) {
  if (!documentId) return;
  try {
    const ids = getEmailForwardWarningShownDocIds();
    if (ids.includes(documentId)) return;
    ids.push(documentId);
    sessionStorage.setItem(EMAIL_FORWARD_WARNING_SHOWN_KEY, JSON.stringify(ids));
  } catch (e) {
    console.warn('Failed to persist email forward warning flag:', e);
  }
}

/** Warnings shown when a document is opened: other viewers, and email-forwarding guidance. */
const useDocumentEntryWarnings = ({ doc, user, getViewers, loadKey }) => {
  const [showViewersWarningModal, setShowViewersWarningModal] = useState(false);
  const [showEmailForwardWarningModal, setShowEmailForwardWarningModal] = useState(false);
  const viewersWarningShownRef = useRef(new Set());

  const docId = doc?.documentId;
  const senderEmail = doc?.senderEmail;
  const userId = user?.userId;

  useEffect(() => {
    setShowViewersWarningModal(false);
    setShowEmailForwardWarningModal(false);
  }, [loadKey]);

  // Check for existing viewers once per document. The shown-set is what stops a later change in
  // viewers from reopening the warning for a document already checked.
  useEffect(() => {
    if (!docId || !getViewers || !userId) return;

    if (viewersWarningShownRef.current.has(docId)) return;
    viewersWarningShownRef.current.add(docId);

    const viewers = getViewers(docId);
    const otherViewers = viewers.filter(viewer => viewer.userId !== userId);

    if (otherViewers.length > 0) {
      console.log(`Document ${docId} has ${otherViewers.length} other viewers (total: ${viewers.length}), showing warning modal`);
      setShowViewersWarningModal(true);
    } else {
      console.log(`Document ${docId} has no other viewers (total: ${viewers.length}, current user: ${userId})`);
    }
  }, [docId, getViewers, userId]);

  // Email forwarding guidance once per document per browser session (sessionStorage; survives leaving detail view)
  useEffect(() => {
    if (!docId || !senderEmail) {
      setShowEmailForwardWarningModal(false);
      return;
    }
    if (isEmailForwardWarningMarkedShown(docId)) return;
    setShowEmailForwardWarningModal(true);
  }, [docId, senderEmail]);

  const dismissViewersWarning = useCallback(() => setShowViewersWarningModal(false), []);

  const dismissEmailForwardWarning = useCallback(() => {
    markEmailForwardWarningShown(docId);
    setShowEmailForwardWarningModal(false);
  }, [docId]);

  return {
    showViewersWarningModal,
    dismissViewersWarning,
    showEmailForwardWarningModal,
    dismissEmailForwardWarning,
  };
};

export default useDocumentEntryWarnings;
